
import React from "react";
import { Check, BookOpen, Download, Users, ArrowRight } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";
import EscapeButton from "@/components/EscapeButton";

const ManualEscape: React.FC = () => {
  useScrollReveal();

  const modules = [
    {
      title: "Aula 1 – Rotina de Alta Performance",
      description: "Como criar uma rotina mínima que realmente funciona (sem frescura)"
    },
    {
      title: "Aula 2 – 5 Hábitos Transformadores",
      description: "Os hábitos que mudaram minha vida e podem mudar a sua"
    },
    {
      title: "Aula 3 – Guia Prático Anti-Paralisia",
      description: "Como sair da paralisia em 3 passos simples e eficazes"
    },
    {
      title: "Aula 4 – Checklist Diário Escape",
      description: "Material para imprimir e usar todos os dias"
    }
  ];

  const targetAudience = [
    "Vive travando na hora de agir",
    "Se sente preso no modo 'espectador'",
    "Quer disciplina sem precisar virar um robô",
    "Tá cansado de se enrolar e não sair do lugar"
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Hero Section */}
      <section className="py-20 px-6 md:px-10 max-w-4xl mx-auto text-center">
        <div className="scroll-reveal">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-8">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-medium">Manual do Escape</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            Como sair da inércia e assumir o controle da sua vida
          </h1>
          
          <p className="text-xl md:text-2xl opacity-80 mb-8 max-w-3xl mx-auto leading-relaxed">
            Se você está travado, sem energia, se enrolando para começar as coisas... 
            esse material é para você.
          </p>
          
          <p className="text-lg opacity-70 mb-12 max-w-2xl mx-auto">
            Aqui, você vai aprender o que realmente funciona para parar de se sabotar 
            e voltar a sentir orgulho de quem você é. Sem papo furado. Sem teoria de livro. 
            <strong className="text-white"> Só o que muda sua vida quando você aplica.</strong>
          </p>
        </div>
      </section>

      {/* Inspirational Image Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <div className="relative rounded-lg overflow-hidden mb-8">
            <img
              src="/lovable-uploads/78497823-116f-4c93-b5a4-fdcd2527caef.png"
              alt="Escape do óbvio - Transformação começa aqui"
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-center">
              <p className="text-xl md:text-2xl font-bold text-white mb-2">
                "ORDINARY DIES HERE"
              </p>
              <p className="text-lg text-white/80">
                O lugar onde a mediocridade não tem vez
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Receive */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            💥 O que você vai receber
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {modules.map((module, index) => (
              <div
                key={module.title}
                className="scroll-reveal bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className="bg-green-500/20 p-2 rounded-full flex-shrink-0">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{module.title}</h3>
                    <p className="text-sm opacity-70">{module.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="scroll-reveal bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-400/30 rounded-lg p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Download className="w-5 h-5 text-blue-400" />
              <span className="font-semibold">Bônus Exclusivo</span>
            </div>
            <p className="opacity-80">PDFs completos de apoio e prática para você implementar tudo</p>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            📌 Para quem é esse minicurso?
          </h2>
          
          <p className="text-xl mb-8 text-center opacity-80">Para quem:</p>
          
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {targetAudience.map((item, index) => (
              <div
                key={item}
                className="scroll-reveal flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg p-4"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-red-500/20 p-1 rounded-full flex-shrink-0">
                  <ArrowRight className="w-4 h-4 text-red-400" />
                </div>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            🎯 Por que esse conteúdo é diferente?
          </h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-8 max-w-2xl mx-auto text-center">
            <p className="text-lg mb-4 leading-relaxed">
              <strong className="text-white">Porque eu vivo o que ensino.</strong>
            </p>
            <p className="text-lg mb-4 leading-relaxed opacity-80">
              Não estou aqui para te motivar com frases prontas.
            </p>
            <p className="text-lg mb-4 leading-relaxed opacity-80">
              Estou aqui para te mostrar que você consegue, desde que comece certo.
            </p>
            <p className="text-lg leading-relaxed">
              <strong className="text-white">Esse material é o empurrão que talvez você só precisava.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <div className="bg-gradient-to-r from-green-500/20 to-green-400/10 border border-green-400/30 rounded-lg p-8 max-w-md mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">💸 Investimento</h3>
            
            <div className="mb-6">
              <div className="text-sm opacity-60 line-through mb-2">De R$97,00 por</div>
              <div className="text-4xl font-bold text-green-400 mb-2">R$29,90</div>
              <div className="text-sm opacity-80">Pagamento único • Acesso vitalício</div>
            </div>
            
            <div className="bg-black/20 rounded-lg p-4 mb-6">
              <p className="text-sm mb-2 opacity-80">Pense assim:</p>
              <p className="text-sm mb-1">Se isso aqui já é transformador...</p>
              <p className="text-sm font-semibold text-green-400">Imagina o que vem por aí na comunidade.</p>
            </div>
            
            <EscapeButton 
              size="large" 
              className="w-full"
              href="#"
            >
              QUERO COMEÇAR AGORA
            </EscapeButton>
            
            <p className="text-xs opacity-60 mt-4">
              Sem desculpa. Sem enrolação. Acesso imediato.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-sm opacity-50 border-t border-white/10">
        <p>© {new Date().getFullYear()} ESCAPE CLUB. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default ManualEscape;
